import { Col, Row } from "reactstrap";
import { useCallback, useMemo } from "react";
import {
  IWorkInfoDetails,
  ModalType,
} from "../../../../../Types/UserProfileDetails";
import { workInformationModal } from "../../../../../utils/Constant";
import { useAppDispatch } from "../../../../../ReduxToolkit/Hooks";
import { openModal } from "../../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import EmployeeInfoCard from "../../EmployeeInfoCard";
import { findAndCategorizedAttachment } from "../../../../../utils/fileUtils";
import { setAttachment } from "../../../../../ReduxToolkit/Slice/AttachmentSlice";
import { useEmployeeProfile } from "../../../../../hooks/useEmployeeProfile";
import { dateConversion } from "../../../../../utils/dateUtils";
import { canShowEditInfo, hasAccessPrivateFields } from "../../../utils/access";

const OMIT_KEYS: Array<keyof IWorkInfoDetails | string> = [
  "id",
  "user_id",
  "addedby",
  "updatedby",
  "created_at",
  "updated_at",
  "sip",
];

const LABEL_MAP: Partial<Record<keyof IWorkInfoDetails | string, string>> = {
  designation1: "Designation",
  fromdate: "Joining Date",
  todate: "Relieving Date",
  offerletter: "Offer Letter",
  relievingletter: "Relieving Letter",
};

const PREVIEW_MODAL_BY_KEY: Record<string, ModalType> = {
  offerletter: "offerLetterModal",
  relievingletter: "relievingLetterModal",
};

const EmployeeWorkInfo = () => {
  const dispatch = useAppDispatch();
  const { employeeProfileDetails, loggedInEmployee } = useEmployeeProfile();

  const employeeWorkInfo =
    employeeProfileDetails?.employeeWorkInformation ?? [];

  const canEdit = useMemo(
    () =>
      !!employeeProfileDetails &&
      !!loggedInEmployee &&
      canShowEditInfo(employeeProfileDetails, loggedInEmployee),
    [employeeProfileDetails, loggedInEmployee]
  );

  const employeeId = employeeProfileDetails?.jobInformation?.userId;
  const viewerId = loggedInEmployee?.id;
  const viewerRole = loggedInEmployee?.user_type;

  const hasAccess = useMemo(() => {
    if (employeeId == null || viewerId == null || !viewerRole) return false;
    return hasAccessPrivateFields(
      Number(employeeId),
      Number(viewerId),
      viewerRole
    );
  }, [employeeId, viewerId, viewerRole]);

  const handleOpenModal = useCallback(
    (modalType: ModalType, selectedId?: number) => {
      dispatch(openModal({ modalType, selectedId }));
    },
    [dispatch]
  );

  const formatters = useMemo(
    () =>
      hasAccess
        ? ({
            fromdate: (v: any) => dateConversion(v, false, true),
            todate: (v: any) => dateConversion(v, false, true),
          } as const)
        : ({} as const),
    [hasAccess]
  );

  const previewableKeys = useMemo(
    () => (hasAccess ? (["offerletter", "relievingletter"] as const) : []),
    [hasAccess]
  );

  const handlePreview = useCallback(
    (key: string, value: unknown) => {
      if (typeof value !== "string") return;
      const file = findAndCategorizedAttachment(value);
      if (!file) return;

      dispatch(setAttachment(file));
      dispatch(
        openModal({
          modalType: PREVIEW_MODAL_BY_KEY[key] ?? "offerLetterModal",
        })
      );
    },
    [dispatch]
  );

  if (!employeeWorkInfo.length) return <Row />;

  return (
    <Row>
      {employeeWorkInfo.map((work) => (
        <Col
          md="6"
          key={
            work.id ?? `${work.designation1}-${work.fromdate}-${work.todate}`
          }
          className="mb-3"
        >
          <EmployeeInfoCard<IWorkInfoDetails>
            title="Work Information"
            entity={work}
            showEditInfo={canEdit}
            canEdit={canEdit}
            bodyClassName="p-3"
            labelMap={LABEL_MAP}
            omitKeys={OMIT_KEYS}
            iconOnClick={() =>
              handleOpenModal(workInformationModal, work.id ?? undefined)
            }
            previewableKeys={[...previewableKeys]}
            formatters={formatters as any}
            onPreview={handlePreview}
          />
        </Col>
      ))}
    </Row>
  );
};

export default EmployeeWorkInfo;
